/**
 * Metric type ↔ kolon uyumluluk helper'ları.
 *
 * Backend `/rule-engine/metadata/ch/tables/{table}/columns` artık her kolon
 * için iki yeni field döndürüyor:
 *   • category          : "numeric" | "date" | "text" | "boolean" | "other"
 *   • aggregatable_with : ["SUM","AVG","MIN","MAX","COUNT_DISTINCT","RATIO","RANK"]
 *
 * Bu modül o field'ları frontend'de tüketerek dropdown filtrelemesi ve
 * uyumsuz seçimleri otomatik temizleme işlerini tek noktada toplar.
 *
 * Backend henüz yeni kontratı dönmüyorsa (deploy sırası) eski davranışa
 * (`c.aggregatable !== false`) düşer — kırılma olmaz.
 */

/**
 * Bir kolon, verilen metric_type ile aggregate yapılabilir mi?
 *
 * @param {object} col — { name, key, type, aggregatable?, aggregatable_with? }
 * @param {string} metricType — "SUM" | "AVG" | "COUNT" | "COUNT_DISTINCT" | ...
 * @returns {boolean}
 */
export function isColumnAggregatable(col, metricType) {
  if (!col) return false;
  // COUNT kolon istemez — her kolon "uyumlu" sayılır (zaten kullanılmıyor)
  if (metricType === 'COUNT') return true;

  // Yeni backend kontratı
  if (Array.isArray(col.aggregatable_with)) {
    return col.aggregatable_with.includes(metricType);
  }

  // Eski backend fallback (boolean alan)
  return col.aggregatable !== false;
}

/**
 * Verilen metric_type için seçilebilecek kolonları filtreler.
 *
 * @param {Array}  columns
 * @param {string} metricType
 * @returns {Array}
 */
export function filterColumnsForMetric(columns, metricType) {
  if (!Array.isArray(columns)) return [];
  return columns.filter((c) => isColumnAggregatable(c, metricType));
}

/**
 * Kolon select'inde label'ın yanına tip etiketini parantez içinde ekle.
 *
 * UI sadeleştirmesi (müşteri geri bildirimi): "boş olabilir" gibi
 * nullable bilgisini gösterme — kolonların büyük çoğunluğu zaten
 * nullable, tek tek belirtmek bilgi yoğunluğunu artırıyor. Tip kısa
 * metin parantez içinde gösteriliyor (örn. "kullanici_kodu (Metin)").
 *
 * Backend `type_label` alanı (Türkçe; örn. "Sayı", "Metin", "Tarih")
 * varsa onu tercih ediyoruz; yoksa CH tipini "Nullable(...)" sarmalını
 * temizleyerek gösteriyoruz.
 *
 * @param {object} col
 * @returns {string}
 */
export function decorateColumnLabel(col) {
  if (!col) return '';
  const baseLabel = col.label || col.name || col.key || '';
  // type_label backend'den Türkçe gelir (Sayı / Metin / Tarih / Karar);
  // yoksa raw `type`'ı Nullable() sarmasından arındırarak kullanıyoruz.
  const rawType = col.type_label || (col.type || '').replace(/^Nullable\(([^)]+)\)$/, '$1');
  return rawType ? `${baseLabel} (${rawType})` : baseLabel;
}

/**
 * Mevcut seçili kolon, yeni metric_type ile uyumsuz hâle geldi mi?
 * Form mantığında metric_type değişince kolonu otomatik temizlemek için.
 *
 * @param {Array}  columns        — tablonun tüm kolonları
 * @param {string} columnName     — şu an seçili kolon adı
 * @param {string} newMetricType
 * @returns {boolean}
 */
export function selectionIsIncompatible(columns, columnName, newMetricType) {
  if (!columnName) return false;
  const col = (columns || []).find((c) => (c.name || c.key) === columnName);
  if (!col) return false;
  return !isColumnAggregatable(col, newMetricType);
}

/* -------- Filtre değer kutusu: kolon tipine göre input tipi -------- */

const _rawType = (col) => String(col?.type || '').replace(/^Nullable\((.+)\)$/, '$1');

/** Kolon tarih/zaman mı? (backend `category: "date"` ya da CH tipi Date/DateTime*) */
export function isDateColumn(col) {
  if (!col) return false;
  if (col.category) return col.category === 'date';
  return /^Date/i.test(_rawType(col));
}

/** Kolon sayısal mı? (backend `category: "numeric"` ya da CH Int/UInt/Float/Decimal) */
export function isNumericColumn(col) {
  if (!col) return false;
  if (col.category) return col.category === 'numeric';
  return /^(U?Int\d+|Float\d+|Decimal)/i.test(_rawType(col));
}

/**
 * Filtre satırındaki değer kutusunun HTML input tipi.
 *   • `${SABIT}` referansı ve IN / NOT IN (virgüllü liste) → 'text'
 *   • tarih kolonu → 'date'  (değer YYYY-MM-DD; backend string bağlar, CH tarihe çevirir)
 *   • sayısal kolon → 'number'
 *   • diğer → 'text'
 */
export function filterValueInputType(col, op, value) {
  const v = typeof value === 'string' ? value : '';
  if (v.startsWith('${') && v.endsWith('}')) return 'text';
  const o = String(op || '=').toUpperCase();
  if (o === 'IN' || o === 'NOT IN') return 'text';
  if (isDateColumn(col)) return 'date';
  if (isNumericColumn(col)) return 'number';
  return 'text';
}
